import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const AFFILIATE_CODE_KEY = 'affiliate_code';
const AFFILIATE_TIMESTAMP_KEY = 'affiliate_timestamp';
const AFFILIATE_CLICK_TRACKED_KEY = 'affiliate_click_tracked';

// Affiliate code expires after 30 days
const AFFILIATE_CODE_EXPIRY_DAYS = 30;

export const useAffiliateTracking = () => {
  const trackAffiliateClick = useCallback(async (code: string) => {
    try {
      await supabase.functions.invoke('track-affiliate-click', {
        body: {
          affiliateCode: code,
          referrer: document.referrer || null,
          userAgent: navigator.userAgent,
        },
      });
      console.log('[Affiliate] Click tracked for code:', code);
    } catch (error) {
      console.error('[Affiliate] Error tracking click:', error);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    if (refCode) {
      // Save to localStorage
      localStorage.setItem(AFFILIATE_CODE_KEY, refCode);
      localStorage.setItem(AFFILIATE_TIMESTAMP_KEY, Date.now().toString());

      // Track the click (once per session)
      if (!sessionStorage.getItem(AFFILIATE_CLICK_TRACKED_KEY)) {
        trackAffiliateClick(refCode);
        sessionStorage.setItem(AFFILIATE_CLICK_TRACKED_KEY, 'true');
      }

      // Clean URL by removing the ref parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('ref');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [trackAffiliateClick]);

  const getAffiliateCode = useCallback((): string | null => {
    const code = localStorage.getItem(AFFILIATE_CODE_KEY);
    const timestamp = localStorage.getItem(AFFILIATE_TIMESTAMP_KEY);

    if (!code || !timestamp) {
      return null;
    }

    // Check if the code has expired
    const savedTime = parseInt(timestamp, 10);
    const expiryTime = AFFILIATE_CODE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    if (Date.now() - savedTime > expiryTime) {
      // Code has expired, clean up
      localStorage.removeItem(AFFILIATE_CODE_KEY);
      localStorage.removeItem(AFFILIATE_TIMESTAMP_KEY);
      return null;
    }

    return code;
  }, []);

  const clearAffiliateCode = useCallback(() => {
    localStorage.removeItem(AFFILIATE_CODE_KEY);
    localStorage.removeItem(AFFILIATE_TIMESTAMP_KEY);
    sessionStorage.removeItem(AFFILIATE_CLICK_TRACKED_KEY);
  }, []);

  return {
    affiliateCode: getAffiliateCode(),
    hasAffiliate: !!getAffiliateCode(),
    clearAffiliateCode,
  };
};

// Coupon code for affiliate referrals
export const AFFILIATE_FRIEND_COUPON = 'FRIEND';

// Utility function to get affiliate code without hook (for use in form submissions)
export const getStoredAffiliateCode = (): string | null => {
  const code = localStorage.getItem(AFFILIATE_CODE_KEY);
  const timestamp = localStorage.getItem(AFFILIATE_TIMESTAMP_KEY);

  if (!code || !timestamp) {
    return null;
  }

  const savedTime = parseInt(timestamp, 10);
  const expiryTime = AFFILIATE_CODE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  if (Date.now() - savedTime > expiryTime) {
    localStorage.removeItem(AFFILIATE_CODE_KEY);
    localStorage.removeItem(AFFILIATE_TIMESTAMP_KEY);
    return null;
  }

  return code;
};

// Get the friend coupon code if affiliate code exists
export const getAffiliateCouponCode = (): string | null => {
  const affiliateCode = getStoredAffiliateCode();
  return affiliateCode ? AFFILIATE_FRIEND_COUPON : null;
};
